<template>
  <base-content>
    <div class="tw-flex tw-flex-col tw-mb-4">
      <h1 class="tw-text-xl md:tw-text-2xl tw-font-thin tw-mb-4">Operations Summary</h1>
      <div class="tw-grid tw-gap-3" style="grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));">
        <active-prescriptions v-if="organization.usePrescriptionProgramSetting" :business-id="businessId" />
        <active-plans :business-id="businessId" />
        <activities
          v-if="organization.useTodoSetting && !isLocationFiltered"
          :business-id="businessId"
          :summary-data="operationsSummary"
          :is-summary-loading="isLoadingOperationsSummary"
        />
        <awaiting-onboarding
          v-if="organization.usePrescriptionProgramSetting && !isLocationFiltered"
          :summary-data="operationsSummary"
          :is-summary-loading="isLoadingOperationsSummary"
        />
        <delayed-start
          v-if="organization.usePrescriptionProgramSetting && !isLocationFiltered"
          :summary-data="operationsSummary"
          :is-summary-loading="isLoadingOperationsSummary"
        />
        <charges :business-id="businessId" v-if="isLocationFiltered || businessId !== rxHarmonyBusinessId" />
        <active-disputes v-if="isAdministrator && !isLocationFiltered && businessId !== rxHarmonyBusinessId" :business-id="businessId" />
      </div>
      <div v-if="organization.usePrescriptionProgramSetting && !isLoadingOperationsSummary && !isLocationFiltered" class="tw-mt-4 tw-text-sm tw-font-normal tw-text-gray-500">
        Awaiting Onboarding ({{ operationsSummary.awaitingOnboarding.toLocaleString() }}) + Delayed Start ({{ operationsSummary.delayedStart.toLocaleString() }}) =
        {{ operationsSummary.explainedGapCount.toLocaleString() }} - {{ operationsSummary.explainedGapCount == operationsSummary.gapCount ? "" : "does not" }} matches gap between
        Active Prescriptions and Active Plans ({{ operationsSummary.gapCount.toLocaleString() }})
      </div>

      <!-- Activity Summary Section with Global Filter -->
      <div v-if="isLocationFiltered && cobrandId === 'rx-harmony'" v-show="false" class="tw-flex tw-flex-col tw-mt-6 tw-mb-4">
        <div class="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <h1 class="tw-text-xl md:tw-text-2xl tw-font-thin">Activity Summary</h1>
        </div>

        <!-- Global Date Filter -->
        <div class="tw-bg-white tw-rounded-lg tw-shadow-sm tw-p-4 tw-mb-4">
          <div class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">Due Date</div>
          <div class="tw-flex tw-flex-wrap tw-items-start tw-gap-3">
            <base-button-toggle-group :value="activeFilter" size="xs" :options="dateShortcuts" @selected="onDateFilterSelected" :disabled="isLoadingActivitySummary" />

            <!-- Custom Date Range Fields -->
            <div v-show="showDateFields" class="tw-flex tw-flex-wrap tw-gap-3 tw-items-end">
              <base-date-picker label="Start Date" v-model="startDate" :typeable="true" :inline="false" :min-date="getMinStartDate()" />
              <base-date-picker label="End Date" v-model="endDate" :typeable="true" :inline="false" :min-date="getMinEndDate()" />
              <base-button class="tw-w-auto" @click="applyCustomFilter" :disabled="!startDate || !endDate || isLoadingActivitySummary">
                Apply
              </base-button>
            </div>

            <!-- Clear Filters Button -->
            <base-button type="flat" class="tw-w-auto tw-ml-auto" @click="clearFilters" v-if="activeFilter !== 'week'" :disabled="isLoadingActivitySummary">
              Clear Filters
            </base-button>
          </div>
        </div>

        <!-- Activity Summary Cards  -->
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 md:tw-grid-cols-4 tw-gap-4 tw-mb-4">
          <activity-summary-card
            title="Activities Due"
            :business-id="businessId"
            :activity-data="activitiesDueData"
            :start-date="filterStartDate"
            :end-date="filterEndDate"
            :active-filter="activeFilter"
            :is-loading="isLoadingActivitySummary"
          />
          <activity-summary-card
            title="Total Activities"
            :business-id="businessId"
            :activity-data="totalActivitiesData"
            :start-date="filterStartDate"
            :end-date="filterEndDate"
            :active-filter="activeFilter"
            :is-loading="isLoadingActivitySummary"
          />
          <activity-summary-card
            title="Refill Authorization"
            :business-id="businessId"
            :activity-data="activitySummary.refillAuthorization"
            :start-date="filterStartDate"
            :end-date="filterEndDate"
            :active-filter="activeFilter"
            :is-loading="isLoadingActivitySummary"
          />
          <activity-summary-card
            title="Prescriber Clarifications"
            :business-id="businessId"
            :activity-data="activitySummary.prescriberClarification"
            :start-date="filterStartDate"
            :end-date="filterEndDate"
            :active-filter="activeFilter"
            :is-loading="isLoadingActivitySummary"
          />
        </div>
        <!-- Revenue Trend -->
        <div class="tw-mt-6 tw-mb-4">
          <h1 class="tw-text-xl md:tw-text-2xl tw-font-thin tw-mb-4">Revenue Trend (Month by Month)</h1>
          <provider-monthly-revenue-with-projection :business-id="businessId" />
        </div>
        <!-- Revenue Trend -->
        <div class="tw-mt-6 tw-mb-4">
          <h1 class="tw-text-xl md:tw-text-2xl tw-font-thin tw-mb-4">Revenue Trend (Day by Day)</h1>
          <provider-daily-revenue-with-projection :business-id="businessId" />
        </div>
        <!-- Churn Summary -->
        <div class="tw-mt-6 tw-hidden">
          <h1 class="tw-text-xl md:tw-text-2xl tw-font-thin tw-mb-4">Churn Summary</h1>
          <churn-projection-summary :business-id="businessId" />
        </div>
      </div>

      <div class="tw-mt-6 tw-mb-4">
        <h1 class="tw-text-xl md:tw-text-2xl tw-font-thin tw-mb-4">Practice Summary</h1>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
          <plan-counts-by-group :business-id="businessId" />
          <prescription-counts-by-group v-if="organization.usePrescriptionProgramSetting" :business-id="businessId" />
          <activities-by-type v-if="organization.useTodoSetting" :business-id="businessId" />
        </div>
      </div>

      <div v-if="isAdministrator && !isLocationFiltered" class="tw-mt-6 tw-mb-4">
        <h1 class="tw-text-xl md:tw-text-2xl tw-font-thin tw-mb-4">Revenue Summary</h1>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
          <revenue-trend :business-id="businessId" />
          <monthly-revenue :business-id="businessId" />
        </div>
      </div>
    </div>
    <provider-notifications-modal v-if="isLocationFiltered" ref="notificationModal" :business-id="businessId" :initial-items="unreadNotifications" />
  </base-content>
</template>
<script>
import { mapGetters } from "vuex";
import ActivePlans from "@/views/business/dashboard/components/ActivePlans";
import ActivePrescriptions from "@/views/business/dashboard/components/ActivePrescriptions";
import Activities from "@/views/business/dashboard/components/Activities";
import ActivitiesByType from "@/views/business/dashboard/components/ActivitiesByType";
import Charges from "@/views/business/dashboard/components/Charges";
import ActiveDisputes from "@/views/business/dashboard/components/ActiveDisputes";
import AwaitingOnboarding from "@/views/business/dashboard/components/AwaitingOnboarding.vue";
import DelayedStart from "@/views/business/dashboard/components/DelayedStart.vue";
import PlanCountsByGroup from "@/views/business/dashboard/components/PlanCountsByGroup";
import PrescriptionCountsByGroup from "@/views/business/dashboard/components/PrescriptionCountsByGroup";
import RevenueTrend from "@/views/business/dashboard/components/RevenueTrend";
import MonthlyRevenue from "@/views/business/dashboard/components/MonthlyRevenue";
import ProviderNotificationsModal from "@/views/business/support/components/ProviderNotificationsModal.vue";
import ActivitySummaryCard from "@/views/business/dashboard/components/ActivitySummaryCard";
import ProviderDailyRevenueWithProjection from "@/views/business/dashboard/components/ProviderDailyRevenueWithProjection";
import ChurnProjectionSummary from "@/views/business/dashboard/components/ChurnProjectionSummary";
import ProviderMonthlyRevenueWithProjection from "@/views/business/dashboard/components/ProviderMonthlyRevenueWithProjection";

import { capitalize } from "lodash";
import moment from "moment";

const TAKE_KEY = "predictile_business_unread_notifications_take";

export default {
  name: "BusinessDashboardView",
  components: {
    ActivePlans,
    ActivePrescriptions,
    Activities,
    ActivitiesByType,
    Charges,
    ActiveDisputes,
    AwaitingOnboarding,
    DelayedStart,
    PlanCountsByGroup,
    PrescriptionCountsByGroup,
    RevenueTrend,
    MonthlyRevenue,
    ProviderNotificationsModal,
    ActivitySummaryCard,
    ProviderDailyRevenueWithProjection,
    ChurnProjectionSummary,
    ProviderMonthlyRevenueWithProjection,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      rxHarmonyBusinessId: "840b1480-1439-4aa4-9e4b-0fdba48add55",
      unreadNotifications: [],
      isLoading: false,
      isLoadingOperationsSummary: false,
      isLoadingActivitySummary: false,
      lastFilterValue: "month",
      filterRequestTimeout: null,
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      startDate: null,
      endDate: null,
      showDateFields: false,
      activeFilter: "month",
      filterStartDate: moment()
        .startOf("isoWeek")
        .format("YYYY-MM-DD"),
      filterEndDate: moment()
        .endOf("isoWeek")
        .format("YYYY-MM-DD"),
      dateShortcuts: [
        { label: "Past Due", value: "pastDue" },
        { label: "Today", value: "today" },
        { label: "This Week", value: "week" },
        { label: "This Month", value: "month" },
        { label: "Custom", value: "custom" },
      ],
      activitySummary: {
        refillAuthorization: { count: 0, typeFilterId: null },
        prescriberClarification: { count: 0, typeFilterId: null },
        clinicalResponse: { count: 0, typeFilterId: null },
        totalActivities: { count: 0, typeFilterId: null },
        activitiesDue: { count: 0, typeFilterId: null },
      },
      operationsSummary: {
        activitiesDue: 0,
        activitiesPastDue: 0,
        activitiesDueToday: 0,
        awaitingOnboarding: 0,
        delayedStart: 0,
        gapCount: 0,
        explainedGapCount: 0,
      },
    };
  },
  computed: {
    ...mapGetters("role", ["isBusinessAdministrator", "isPredictile", "isLocationFiltered"]),
    ...mapGetters("organization", ["organization", "cobrandId"]),
    isAdministrator() {
      return this.isBusinessAdministrator(this.businessId) || this.isPredictile();
    },
    totalActivitiesData() {
      return this.activitySummary.totalActivities || { count: 0, typeFilterId: null };
    },
    activitiesDueData() {
      return this.activitySummary.activitiesDue || { count: 0, typeFilterId: null };
    },
  },
  watch: {
    isLocationFiltered: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.cobrandId === "rx-harmony") {
          this.activeFilter = "month";
          this.lastFilterValue = "month";
          this.applyDateFilter("month");

          this.$nextTick(() => {
            this.loadActivitySummary();
          });
        }
      },
    },
  },
  beforeDestroy() {
    if (this.filterRequestTimeout) {
      clearTimeout(this.filterRequestTimeout);
    }
  },
  mounted() {
    if (!sessionStorage.getItem("notificationChecked")) {
      this.checkUnreadNotifications();
      sessionStorage.setItem("notificationChecked", "true");
    }

    this.loadOperationsSummary();

    this.activeFilter = "month";
    this.lastFilterValue = "month";
    this.applyDateFilter("month");

    if (this.isLocationFiltered && this.cobrandId === "rx-harmony") {
      this.$nextTick(() => {
        this.loadActivitySummary();
      });
    }
  },
  methods: {
    capitalize,

    async loadOperationsSummary() {
      this.isLoadingOperationsSummary = true;

      try {
        const res = await this.$axios.get(`/businesses/${this.businessId}/dashboard/operations-summary`);
        this.operationsSummary = {
          ...this.operationsSummary,
          ...res.data,
        };
      } finally {
        this.isLoadingOperationsSummary = false;
      }
    },

    onDateFilterSelected(value) {
      // Prevent duplicate request if same filter is clicked twice
      if (value === this.lastFilterValue && this.activeFilter === value) {
        return;
      }

      this.lastFilterValue = value;
      this.dateShortcutSelected(value);
    },

    dateShortcutSelected(value) {
      this.activeFilter = value;
      this.showDateFields = value === "custom";

      if (value === "custom") {
        return;
      }

      this.startDate = null;
      this.endDate = null;
      this.applyDateFilter(value);
    },

    applyDateFilter(filterType) {
      const now = moment();

      switch (filterType) {
        case "pastDue":
          this.filterStartDate = null;
          this.filterEndDate = now
            .clone()
            .subtract(1, "day")
            .endOf("day")
            .format("YYYY-MM-DD");
          break;

        case "today":
          this.filterStartDate = now.startOf("day").format("YYYY-MM-DD");
          this.filterEndDate = now.endOf("day").format("YYYY-MM-DD");
          break;

        case "yesterday":
          this.filterStartDate = now
            .clone()
            .subtract(1, "day")
            .startOf("day")
            .format("YYYY-MM-DD");
          this.filterEndDate = now
            .clone()
            .subtract(1, "day")
            .endOf("day")
            .format("YYYY-MM-DD");
          break;

        case "week":
          this.filterStartDate = now.startOf("isoWeek").format("YYYY-MM-DD");
          this.filterEndDate = now.endOf("isoWeek").format("YYYY-MM-DD");
          break;

        case "month":
          this.filterStartDate = now.startOf("month").format("YYYY-MM-DD");
          this.filterEndDate = now.endOf("month").format("YYYY-MM-DD");
          break;
      }

      this.debouncedLoadActivitySummary();
    },

    debouncedLoadActivitySummary() {
      if (this.filterRequestTimeout) {
        clearTimeout(this.filterRequestTimeout);
      }

      this.filterRequestTimeout = setTimeout(() => {
        this.loadActivitySummary();
      }, 300);
    },

    showToast(message) {
      if (this.$toast && this.$toast.error) {
        this.$toast.error(message);
      } else {
        console.error(message);
      }
    },

    applyCustomFilter() {
      if (this.activeFilter !== "custom") {
        return;
      }

      if (!this.startDate || !this.endDate) {
        this.showToast("Please select both start and end dates");
        return;
      }

      if (moment(this.startDate).isAfter(moment(this.endDate))) {
        this.showToast("Start date must be before or equal to end date");
        return;
      }

      if (moment(this.endDate).isBefore(moment(this.startDate))) {
        this.showToast("End date must be after or equal to start date");
        return;
      }

      // Validate no past dates (today minimum)
      if (moment(this.startDate).isBefore(moment().startOf("day"))) {
        this.showToast("Start date cannot be in the past");
        return;
      }

      // Convert Date objects to YYYY-MM-DD strings
      this.filterStartDate = moment(this.startDate).format("YYYY-MM-DD");
      this.filterEndDate = moment(this.endDate).format("YYYY-MM-DD");
      this.debouncedLoadActivitySummary();
    },
    getMinStartDate() {
      if (this.activeFilter === "custom") {
        return moment()
          .startOf("day")
          .toDate();
      }
      return null;
    },

    getMinEndDate() {
      if (this.activeFilter === "custom") {
        if (this.startDate) {
          return moment(this.startDate)
            .startOf("day")
            .toDate();
        }
        return moment()
          .startOf("day")
          .toDate();
      }
      return null;
    },

    clearFilters() {
      this.activeFilter = "week";
      this.lastFilterValue = "week";
      this.startDate = null;
      this.endDate = null;
      this.filterStartDate = moment()
        .startOf("isoWeek")
        .format("YYYY-MM-DD");
      this.filterEndDate = moment()
        .endOf("isoWeek")
        .format("YYYY-MM-DD");
      this.showDateFields = false;
      this.debouncedLoadActivitySummary();
    },

    async loadActivitySummary() {
      if (!this.isLocationFiltered && this.cobrandId !== "rx-harmony") {
        return;
      }

      if (this.isLoadingActivitySummary) {
        return;
      }

      this.isLoadingActivitySummary = true;

      try {
        const res = await this.$axios.get(`/businesses/${this.businessId}/dashboard/activity-summary`, {
          params: {
            startDate: this.filterStartDate,
            endDate: this.filterEndDate,
            filterType: this.activeFilter,
          },
        });

        this.activitySummary = {
          refillAuthorization: res.data.refillAuthorization || { count: 0, typeFilterId: null },
          prescriberClarification: res.data.prescriberClarification || { count: 0, typeFilterId: null },
          clinicalResponse: res.data.clinicalResponse || { count: 0, typeFilterId: null },
          totalActivities: res.data.totalActivities || { count: 0, typeFilterId: null },
          activitiesDue: res.data.activitiesDue || { count: 0, typeFilterId: null },
        };
      } catch (err) {
        console.error("Error loading activity summary:", err);
        this.$toast.error("Failed to load activity summary");
      } finally {
        this.isLoadingActivitySummary = false;
      }
    },

    async checkUnreadNotifications() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/provider-notifications/unread?skip=${this.page.skip}&take=${this.page.take}`;
      url += `&locationFilter=${this.isLocationFiltered ? "true" : "false"}`;

      try {
        const res = await this.$axios.get(url);
        const items = res.data.items || [];

        if (items.length > 0) {
          this.unreadNotifications = items;

          this.$nextTick(() => {
            if (this.$refs.notificationModal && this.$refs.notificationModal.show) {
              this.$refs.notificationModal.show();
            }
          });
        }
      } catch (err) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load notifications",
          message: "We had a problem retrieving the list of notifications. Please reload the page to try again.",
          error: err,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
